import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronRight } from 'lucide-react';

// ============================================
// COLOR DATA — Certificate-optimized palette
// ============================================

const SOLID_COLORS = [
  // Row 1: Neutrals
  ['#000000', '#333333', '#555555', '#777777', '#999999', '#BBBBBB', '#DDDDDD', '#FFFFFF'],
  // Row 2: Reds & Pinks
  ['#FF0000', '#E53E3E', '#FC5185', '#FF69B4', '#D53F8C', '#B83280', '#9B2C2C', '#742A2A'],
  // Row 3: Blues
  ['#0066FF', '#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF', '#1E3A5F', '#0EA5E9', '#06B6D4'],
  // Row 4: Greens & Teals
  ['#10B981', '#059669', '#047857', '#065F46', '#14B8A6', '#0D9488', '#6EE7B7', '#34D399'],
  // Row 5: Golds & Certificate colors
  ['#D4AF37', '#B8860B', '#DAA520', '#F59E0B', '#D97706', '#92400E', '#78350F', '#451A03'],
  // Row 6: Purples & Special
  ['#7C3AED', '#6D28D9', '#5B21B6', '#4C1D95', '#8B5CF6', '#A78BFA', '#EC4899', '#F472B6'],
];

const GRADIENT_PRESETS = [
  // Row 1: Dark & Elegant
  { colors: ['#1a1a2e', '#16213e'], name: 'Midnight' },
  { colors: ['#2d2d2d', '#555555'], name: 'Charcoal' },
  { colors: ['#e8e8e8', '#ffffff'], name: 'Silver' },
  { colors: ['#a8ff78', '#78ffd6'], name: 'Lime Mint' },
  { colors: ['#D4AF37', '#F5D76E'], name: 'Gold' },
  { colors: ['#B8860B', '#DAA520'], name: 'Dark Gold' },
  // Row 2: Blues & Certificate
  { colors: ['#667eea', '#764ba2'], name: 'Indigo Purple' },
  { colors: ['#f093fb', '#f5576c'], name: 'Pink Red' },
  { colors: ['#4facfe', '#00f2fe'], name: 'Sky Blue' },
  { colors: ['#43e97b', '#38f9d7'], name: 'Green Teal' },
  { colors: ['#fa709a', '#fee140'], name: 'Sunset' },
  { colors: ['#a18cd1', '#fbc2eb'], name: 'Lavender' },
  // Row 3: Professional
  { colors: ['#1E40AF', '#3B82F6'], name: 'Royal Blue' },
  { colors: ['#0F766E', '#14B8A6'], name: 'Teal' },
  { colors: ['#92400E', '#D97706'], name: 'Bronze' },
  { colors: ['#6B21A8', '#A855F7'], name: 'Purple' },
  { colors: ['#1E293B', '#475569'], name: 'Slate' },
  { colors: ['#DC2626', '#F97316'], name: 'Fire' },
];

// Color name mappings for search
const COLOR_NAMES: Record<string, string> = {
  '#000000': 'Black', '#333333': 'Dark gray', '#555555': 'Gray', '#777777': 'Medium gray',
  '#999999': 'Light gray', '#BBBBBB': 'Silver', '#DDDDDD': 'Light silver', '#FFFFFF': 'White',
  '#FF0000': 'Red', '#0066FF': 'Blue', '#10B981': 'Green', '#D4AF37': 'Gold',
  '#7C3AED': 'Purple', '#F59E0B': 'Amber', '#1E40AF': 'Royal blue', '#1E293B': 'Slate',
  '#DC2626': 'Crimson', '#059669': 'Emerald', '#D97706': 'Bronze', '#06B6D4': 'Cyan',
};

// ============================================
// COMPONENT
// ============================================

interface ColorPaletteProps {
  value: string;
  onChange: (color: string) => void;
  onGradientChange?: (gradient: { colors: string[]; angle?: number }) => void;
  showGradients?: boolean;
  label?: string;
}

/** Calculate dropdown position — centers in viewport for maximum space */
function getDropdownPosition(trigger: HTMLElement | null): React.CSSProperties {
  if (!trigger) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Prefer opening below, but if not enough space, open above
  let top: number;
  if (spaceBelow >= 300) {
    top = rect.bottom + 4;
  } else if (spaceAbove >= 300) {
    top = Math.max(8, rect.top - 400);
  } else {
    // Center vertically if neither side has enough space
    top = Math.max(8, (window.innerHeight - 400) / 2);
  }

  // Keep within horizontal bounds
  const left = Math.min(Math.max(8, rect.left - 80), window.innerWidth - 290);

  return { top, left };
}

export default function ColorPalette({
  value,
  onChange,
  onGradientChange,
  showGradients = false,
  label
}: ColorPaletteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAllSolid, setShowAllSolid] = useState(false);
  const [showAllGradient, setShowAllGradient] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Filter colors by search
  const filteredSolid = search
    ? SOLID_COLORS.flat().filter(c => {
        const name = COLOR_NAMES[c.toUpperCase()] || '';
        return c.toLowerCase().includes(search.toLowerCase()) ||
               name.toLowerCase().includes(search.toLowerCase());
      })
    : null;

  const filteredGradients = search
    ? GRADIENT_PRESETS.filter(g =>
        g.name.toLowerCase().includes(search.toLowerCase()) ||
        g.colors.some(c => c.toLowerCase().includes(search.toLowerCase()))
      )
    : null;

  const visibleSolidRows = showAllSolid ? SOLID_COLORS : SOLID_COLORS.slice(0, 3);
  const visibleGradients = showAllGradient ? GRADIENT_PRESETS : GRADIENT_PRESETS.slice(0, 6);

  return (
    <div ref={containerRef} className="relative">
      {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}

      {/* Color input row */}
      <div className="flex gap-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-9 h-9 rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-colors flex-shrink-0 shadow-sm"
          style={{ backgroundColor: value }}
          title="Open color palette"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="#000000"
        />
      </div>

      {/* Palette dropdown — portal-like positioning */}
      {isOpen && (
        <div
          className="fixed w-72 bg-white rounded-xl shadow-2xl border overflow-hidden"
          style={{ zIndex: 9999, ...getDropdownPosition(containerRef.current) }}
        >
          {/* Search */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Try "blue" or "#00c4cc"'
                className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-3 space-y-4">
            {/* Solid Colors */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Default solid colors
                </span>
                {!search && (
                  <button
                    onClick={() => setShowAllSolid(!showAllSolid)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
                  >
                    {showAllSolid ? 'Show less' : 'See all'}
                    <ChevronRight className={`w-3 h-3 transition-transform ${showAllSolid ? 'rotate-90' : ''}`} />
                  </button>
                )}
              </div>
              {search ? (
                <div className="grid grid-cols-8 gap-1.5">
                  {(filteredSolid || []).map((color) => (
                    <ColorCircle
                      key={color}
                      color={color}
                      isSelected={value.toUpperCase() === color.toUpperCase()}
                      onClick={() => { onChange(color); setIsOpen(false); }}
                      tooltip={COLOR_NAMES[color.toUpperCase()] || color}
                    />
                  ))}
                  {filteredSolid?.length === 0 && (
                    <p className="text-xs text-gray-400 py-2 col-span-8">No matching colors</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {visibleSolidRows.map((row, i) => (
                    <div key={i} className="flex gap-1.5">
                      {row.map((color) => (
                        <ColorCircle
                          key={color}
                          color={color}
                          isSelected={value.toUpperCase() === color.toUpperCase()}
                          onClick={() => { onChange(color); setIsOpen(false); }}
                          tooltip={COLOR_NAMES[color.toUpperCase()] || color}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Gradient Colors */}
            {showGradients && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Default gradient colors
                  </span>
                  {!search && (
                    <button
                      onClick={() => setShowAllGradient(!showAllGradient)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
                    >
                      {showAllGradient ? 'Show less' : 'See all'}
                      <ChevronRight className={`w-3 h-3 transition-transform ${showAllGradient ? 'rotate-90' : ''}`} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-6 gap-1.5">
                  {(search ? filteredGradients || [] : visibleGradients).map((g, i) => (
                    <GradientCircle
                      key={i}
                      colors={g.colors}
                      name={g.name}
                      onClick={() => {
                        if (onGradientChange) {
                          onGradientChange({ colors: g.colors, angle: 135 });
                        }
                        setIsOpen(false);
                      }}
                    />
                  ))}
                  {search && filteredGradients?.length === 0 && (
                    <p className="text-xs text-gray-400 py-2 col-span-6">No matching gradients</p>
                  )}
                </div>
              </div>
            )}

            {/* Custom color picker */}
            <div>
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-2">
                Custom color
              </span>
              <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-8 rounded-lg cursor-pointer border"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function ColorCircle({
  color,
  isSelected,
  onClick,
  tooltip
}: {
  color: string;
  isSelected: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <button
      onClick={onClick}
      title={`${tooltip}\n${color}`}
      className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 flex-shrink-0 ${
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-200 scale-110'
          : color === '#FFFFFF'
            ? 'border-gray-300 hover:border-gray-400'
            : 'border-transparent hover:border-gray-300'
      }`}
      style={{ backgroundColor: color }}
    />
  );
}

function GradientCircle({
  colors,
  name,
  onClick
}: {
  colors: string[];
  name: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${name}\n${colors.join(' → ')}`}
      className="w-7 h-7 rounded-full border-2 border-transparent hover:border-gray-300 transition-all hover:scale-110 flex-shrink-0"
      style={{
        background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`
      }}
    />
  );
}
